import { useMemo, useSyncExternalStore } from 'react';
import {
  getClipBroadcastId,
  getClipDurationInSeconds,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { selectSpeakerAnalysis } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { core } from '@/stores/core';
import { areSpeakerLayoutsEnabled, isSplitsFeatureEnabled, isSimplifiedUploadEnabled } from '@/stores/clip';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useUploadEditorReadinessContext } from '@/context/UploadEditorReadinessContext/UploadEditorReadinessContext';
import useAIAddon from '@/hooks/useAIAddon';

export interface AIEditsAvailability {
  isAIEditsHidden: boolean;
  isAiEditsDisabled: boolean;
  AIEditsDisabledReason?: string;
}

/**
 * Computes whether AI Edits is available for the current clip.
 * Shared by the nav-bar "Co-edit" trigger (ClipHeader) so the gating logic
 * lives in a single place.
 */
export default function useAIEditsAvailability(): AIEditsAvailability {
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const hasAIAddon = useAIAddon();
  const { clipData } = useClipsContext();
  // With simplified upload, layouts are still processing right after upload; AI Edits stays
  // disabled until they're ready (mirrors the pre-relocation gate in ClipCustomizerMenu).
  const { layoutsReady } = useUploadEditorReadinessContext();

  // `areSpeakerLayoutsEnabled` reads speaker analysis from the content-metadata store via a
  // non-reactive `getState()`. The analysis loads asynchronously, so subscribe to it here to
  // re-render once it arrives — otherwise the gating below stays stale and Co-edit never enables.
  // The return value is intentionally unused; the call is only for its subscription side-effect.
  void useContentMetadataStore(selectSpeakerAnalysis(getClipBroadcastId(clipData) ?? ''));
  const isSpeakerAnalysisAvailableForClip = areSpeakerLayoutsEnabled(clipData);

  return useMemo(() => {
    const isNotAudioContent = coreStore.content?.media_type !== 'AUDIO';
    const splitsInTimelineEnabled = isSplitsFeatureEnabled(featureFlags, clipData);
    const shouldShowAIEdits =
      isNotAudioContent &&
      (splitsInTimelineEnabled ||
        !isSplitsInTimelineEnabledPure({
          clip: clipData,
          isSplitsTranslationEnabled: !!featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations]
        }));

    const clipDuration = getClipDurationInSeconds(clipData);
    const isClipDurationWithinAIEditsLimit = clipDuration && clipDuration <= 300;
    const isWaitingForSimplifiedUploadProcessing = isSimplifiedUploadEnabled() && !layoutsReady;

    const isAIEditsEnabled =
      isClipDurationWithinAIEditsLimit && isSpeakerAnalysisAvailableForClip && !isWaitingForSimplifiedUploadProcessing;

    let disabledReason: string | undefined;
    if (isWaitingForSimplifiedUploadProcessing) {
      disabledReason = 'AI edits will be enabled after video is processed.';
    } else if (!isAIEditsEnabled) {
      if (!isClipDurationWithinAIEditsLimit) {
        disabledReason = 'AI Edits only work with shorter (<5 minutes) content for now.';
      } else if (!isSpeakerAnalysisAvailableForClip) {
        disabledReason = 'AI Edits would be available for uploads post complete processing.';
      }
    }

    return {
      isAIEditsHidden: !hasAIAddon || !(featureFlags[FeatureFlagKeys.Use_CL_AI_Edits_Beta] && shouldShowAIEdits),
      isAiEditsDisabled: !isAIEditsEnabled,
      AIEditsDisabledReason: disabledReason
    };
  }, [
    featureFlags,
    clipData,
    coreStore.content?.media_type,
    isSpeakerAnalysisAvailableForClip,
    layoutsReady,
    hasAIAddon
  ]);
}
